/* eslint-disable unused-imports/no-unused-vars */
'use client'

import { useSearchParams } from 'next/navigation'
import { ChangeEvent, useCallback, useState } from 'react'

import { JENJANG_SD } from '@/apis/data/statistik/jenjang'
import { WILAYAH_NASIONAL } from '@/apis/data/statistik/wilayah'
import { District, EducationLevel } from '@/apis/types'

export const generateSearchParam = ({
  customKeyword,
  district,
  jenjang,
  year,
  keyword,
}: {
  district: string
  jenjang: string
  year: string
  keyword: string
  customKeyword?: string
}) => {
  const searchParams = new URLSearchParams()
  if (district) {
    searchParams.set('district', district)
  }
  if (year) {
    searchParams.set('year', year)
  }
  if (jenjang) {
    searchParams.set('jenjang', jenjang)
  }

  // Support custom keyword
  if (typeof customKeyword !== 'undefined') {
    if (customKeyword !== '') {
      searchParams.set('s', customKeyword)
    }
  } else if (keyword) {
    if (keyword !== '') {
      searchParams.set('s', keyword)
    }
  }

  const str = searchParams.toString()
  return str ? `?${str}` : '?'
}

function extractInitialValue(initalValue, key) {
  if (typeof initalValue?.get !== 'undefined') {
    return initalValue.get(key)
  }

  return initalValue?.[key]
}

const LAST_YEAR = new Date().getFullYear() - 1

export const useFilterState = ({ dataFilters }) => {
  const searchParams = useSearchParams()

  const queryDistrict = (searchParams?.get?.('district') as string) || ''
  const queryJenjang = (searchParams?.get?.('jenjang') as string) || ''
  const queryYear = (searchParams?.get?.('year') as string) || ''
  const queryKeyword = (searchParams?.get?.('s') as string) || ''

  const [keyword, setKeyword] = useState<string>(queryKeyword)

  const [selectedWilayah, setSelectedWilayah] =
    useState<District>(WILAYAH_NASIONAL)
  const [selectedJenjang, setSelectedJenjang] =
    useState<EducationLevel>(JENJANG_SD)
  const [selectedTahunAjaran, setSelectedTahunAjaran] = useState<number>(
    parseInt(queryYear, 10) || LAST_YEAR
  )

  const handleChangeJenjang = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    const match = dataFilters?.educationLevel?.find(
      (item: EducationLevel) =>
        `${item.id}` === `${newValue}` || `${item.name}` === `${newValue}`
    )

    if (match) {
      setSelectedJenjang(match)
      return match
    }

    return null
  }

  const handleChangeTahunAjaran = (e: ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value
    const newValueInt = parseInt(`${newValue}`, 10)
    setSelectedTahunAjaran(newValueInt)

    return newValueInt
  }

  const handleChangeWilayah = (newWilayah: District) => {
    setSelectedWilayah(newWilayah)
    return newWilayah
  }

  const handleSetKeyword = (newValue: string) => {
    setKeyword(newValue)
    return newValue
  }

  const getFilterSearchParams = useCallback(
    ({ customKeyword }: { customKeyword?: string }) => {
      return generateSearchParam({
        jenjang: selectedJenjang?.name,
        district: selectedWilayah?.id,
        year: selectedTahunAjaran?.toString() || '',
        keyword,
        customKeyword,
      })
    },
    [selectedJenjang, selectedWilayah, selectedTahunAjaran, keyword]
  )

  const getFilterSearchParamsFromQuery = useCallback(
    ({ customKeyword }: { customKeyword?: string }) => {
      return generateSearchParam({
        jenjang: queryJenjang,
        district: queryDistrict,
        year: queryYear,
        keyword: queryKeyword,
        customKeyword,
      })
    },
    [queryDistrict, queryJenjang, queryKeyword, queryYear]
  )

  return {
    // -- Query params
    queryYear,
    queryJenjang,
    queryDistrict,
    queryKeyword,

    // -- Filters
    keyword,
    selectedWilayah,
    selectedJenjang,
    selectedTahunAjaran,

    // -- Change handlers
    handleChangeJenjang,
    handleChangeTahunAjaran,
    handleChangeWilayah,
    handleSetKeyword,

    // -- Get search params for updating state
    getFilterSearchParams,
    getFilterSearchParamsFromQuery,
  }
}
